import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Loader } from 'components';
import {
  useApplicationQuery,
  useBudgetQuery,
  useClientQuery,
} from 'hooks/webapi';
import {
  FINANCE,
  MANUAL_REVIEW,
  DASHBOARD_REDIRECT,
  PHONE_VERIFICATION,
  REJECTED_OFFER,
  WELCOME_BACK,
  WELCOME_BACK_NEW,
} from 'Router/routes';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import IdentifyEvent from 'hooks/context/EventCollector/events/IdentifyEvent';
import getClient from 'api/getClient';
import getClientEmail from 'api/getClientEmail';
import getClientPhone from 'api/getClientPhone';
import { session } from 'session';

export default function DashboardRedirect() {
  const { refetch: fetchClient } = useClientQuery();
  const navigate = useNavigate();
  const started = useRef(false);
  const { refetch: fetchApplication } = useApplicationQuery();
  const { refetch: fetchBudget } = useBudgetQuery(false);
  const { identify } = useEventCollector();

  useEffect(() => {
    session.confirmLogin();
  }, []);

  useEffect(() => {
    async function handleLogin() {
      started.current = true;

      const [client, email, phone] = await Promise.all([
        getClient(),
        getClientEmail(),
        getClientPhone(),
      ]);

      identify(
        new IdentifyEvent({
          phone,
          email,
          client,
        }),
      );

      if (
        client &&
        (client.status === 'REGISTERED' || client.status === 'VALIDATED')
      ) {
        const { data: application } = await fetchApplication();
        identify(
          new IdentifyEvent({
            application,
          }),
        );
        if (application?.resolutionDetail === 'INTERNAL_BLACKLIST') {
          session.logout({ redirectTo: import.meta.env['VITE_HOMEPAGE_URL'] });
          return;
        }

        // Case for manual pre-check
        if (
          application?.status === 'OPEN' &&
          application?.resolution === 'MANUAL' &&
          application?.resolutionMessage !== 'ASK_FOR_DOCUMENTS'
        ) {
          navigate(MANUAL_REVIEW);
          return;
        }

        if (application?.resolution === 'REJECTED') {
          navigate(REJECTED_OFFER);
          return;
        }

        if (application?.status !== 'OPEN') {
          navigate(WELCOME_BACK_NEW);
          return;
        }

        if (client && !client.mobilePhoneVerified) {
          navigate(PHONE_VERIFICATION, {
            state: {
              from: DASHBOARD_REDIRECT,
            },
          });
          return;
        }

        const { data: budget } = await fetchBudget();

        if (budget === null) {
          navigate(FINANCE, {
            state: {
              from: DASHBOARD_REDIRECT,
            },
          });
          return;
        }

        navigate(WELCOME_BACK);
      }
    }
    if (!started.current) {
      handleLogin();
    }
  }, [navigate, fetchApplication, fetchBudget, fetchClient]);

  return <Loader />;
}
